import { useQuery } from "@tanstack/react-query";
import { agreedPaymentTermDays, paymentTermAgreed, type EInvoiceSummary, type PdfImage } from "@talentkontor/domain";
import { api } from "../../../data";
import type { BrandRow, DealRow, InvoiceRow, LineItemRow, PeriodRow, TalentRow } from "../../../data/queries";
import { orgBankOf, brandPartyOf, orgPartyOf, payoutBankOf, talentPartyOf, type DocInputs } from "../../../model/invoice-document";
import { t } from "../../../lib/sprache";
import { mitServerPruefung, type EInvoicePruefung, type ServerPruefFelder } from "../../../model/e-rechnung";

/** Alles, was die Rechnungsseite braucht, in einem Lader (Rechte prüft die Datenbank). */

export interface InvoiceLineRow {
  id: string;
  position: number;
  description: string;
  quantity: number | null;
  unit_cents: number;
  tax_rate_bp: number | null;
  net_cents: number;
  commission_free: boolean;
}
export interface InvoiceEventRow {
  id: number;
  event: string;
  actor: string | null;
  at: string;
  details: Record<string, unknown> | null;
}
export interface AttachmentRow extends ServerPruefFelder {
  id: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  owner_id?: string;
  /** invoice, receipt, proof, dunning_letter, contract, other (0031) */
  category?: string | null;
  uploaded_by?: string | null;
  /** Steuerprüfung L12 (0446): Prüfsumme der Datei und Kurzfassung der E-Rechnung */
  sha256?: string | null;
  e_invoice?: EInvoiceSummary | null;
  /** E-1 (0448): nach mitServerPruefung die Angabe des Browsers und die Herkunft der Werte */
  e_invoice_browser?: EInvoiceSummary | null;
  pruefung?: EInvoicePruefung | null;
}
export interface OrgPaperRow {
  legal_name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  tax_number: string | null;
  reply_to_email: string | null;
  default_document_language: string | null;
  small_business?: boolean | null;
  logo_path?: string | null;
  brand_color?: string | null;
  talent_payout_mode?: string | null;
  payout_auto_confirm_days?: number | null;
  [key: string]: unknown;
}
/** Stand der Auszahlung einer Creator-Rechnung (invoice_payout_info, 0055) */
export interface PayoutInfo {
  invoice_id: string;
  mode: "fixed_terms" | "after_brand_payment";
  payout_due?: string | null;
  brand_state?: "paid" | "partial" | "open" | "not_invoiced" | "unlinked" | "written_off";
  brand_paid_on?: string | null;
  brand_open_since?: string | null;
  brand_numbers?: string[] | null;
  via_creator?: boolean;
  advance?: "block" | "confirm" | null;
}
export interface BankRow {
  holder: string;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  currency: string;
  is_default: boolean;
  qr_iban?: string | null;
}
export interface ContactRow {
  id: string;
  brand_id: string;
  name: string;
  email: string | null;
  is_invoice_recipient: boolean;
  /** Rollen des Kontakts (invoice, invoice_cc, contract, briefing, insights, approval) */
  roles?: string[] | null;
}
export interface PaymentRow {
  id: string;
  kind: "payment" | "write_off" | "reversal";
  amount_cents: number;
  currency: string;
  paid_on: string;
  reference: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
}
/** Stornorechnung bzw. stornierte Originalrechnung (Bezug in beide Richtungen, 0043) */
export interface LinkedInvoice {
  id: string;
  number: string | null;
  issue_date: string | null;
  status: string;
  gross_cents: number;
  currency: string;
  sent_at?: string | null;
  self_billed?: boolean | null;
  paid_cents?: number | null;
  paid_at?: string | null;
  admin_payment_confirmed_at?: string | null;
}
export interface MemberRow {
  user_id: string;
  display_name: string | null;
}

export interface InvoiceDetail {
  invoice: InvoiceRow;
  lines: InvoiceLineRow[];
  events: InvoiceEventRow[];
  attachments: AttachmentRow[];
  talent: TalentRow | null;
  brand: BrandRow | null;
  deal: DealRow | null;
  period: PeriodRow | null;
  related: InvoiceRow[];
  talentDeals: DealRow[];
  talentPeriods: PeriodRow[];
  /** Zusatzpositionen der Kooperation (auch „Ganze Kooperation“): für den Sollbetrag der Prüfung (GELD-8) */
  dealItems?: LineItemRow[];
  org: OrgPaperRow | null;
  bank: BankRow[];
  contacts: ContactRow[];
  members: MemberRow[];
  payments: PaymentRow[];
  /** Stornorechnungen zu dieser Rechnung */
  creditNotes: LinkedInvoice[];
  /** Originalrechnung, wenn dies eine Stornorechnung ist */
  original: LinkedInvoice | null;
  /** Sammelrechnung (0047): alle Monate, die diese Rechnung abrechnet, mit Kooperation */
  collectivePeriods: { period_id: string; month: string; deal_id: string; deal_title: string }[];
  /** Vertretung und Register der Agentur (Entwurf; festgeschriebene Rechnungen lesen den Schnappschuss) */
  legal: Record<string, unknown> | null;
  /** Auszahlungsweg des Creators (Gutschrift-Entwurf) */
  payout: Record<string, unknown> | null;
  /** Steuerhinweis der Agentur in der Belegsprache (nur für Entwürfe und alte Belege ohne eingefrorenen Text) */
  noticeText: string | null;
  /** Creator-Rechnung an die Agentur: Zahlung der Marke und Fälligkeit der Auszahlung */
  payoutInfo: PayoutInfo | null;
  /** Kleinunternehmer-Verlauf des Creators (0062): für die Prüfung gilt der Stand zum Leistungsdatum */
  taxHistory?: { small_business: boolean; valid_from: string }[];
  /** Agentur der Marke, wenn deren Rechnungen über sie laufen (Rechnungsempfänger; E-Rechnung-Wunsch, 0066) */
  billingAgency?: BrandRow | null;
  /** Belege der Creator-Rechnungen zum selben Monat bzw. zur selben Kooperation (Versand an die Marke, 0080) */
  relatedAttachments?: AttachmentRow[];
  /** Steuerprüfung L14: Text der unterschriebenen Verträge zur Kooperation (Zahlungsklausel, zahlungsfristVereinbart) */
  contractBodies?: string[];
}

export async function loadInvoiceDetail(orgId: string, id: string): Promise<InvoiceDetail | null> {
  const invoice = (await api.select<InvoiceRow>("invoices", { filters: [["id", "eq", id], ["org_id", "eq", orgId]] }))[0];
  if (!invoice) return null;
  const byInvoice = [["invoice_id", "eq", id]] as const;
  const [lines, events, attachments, talent, brandFromRow, deal, period, related, org, bank, members] = await Promise.all([
    api.select<InvoiceLineRow>("invoice_lines", { filters: byInvoice, order: [["position", "asc"]] }),
    api.select<InvoiceEventRow>("invoice_events", { filters: byInvoice, order: [["at", "desc"]] }).catch(() => []),
    // E-1 (0448): sha256 und e_invoice sind nach der serverseitigen Prüfung die Werte des Servers
    api.select<AttachmentRow>("attachments", { filters: [["owner_kind", "eq", "invoice"], ["owner_id", "eq", id], ["deleted_at", "is", null]] }).then((r) => r.map(mitServerPruefung)).catch(() => []),
    invoice.talent_id ? api.select<TalentRow>("talents", { filters: [["id", "eq", invoice.talent_id]] }).then((r) => r[0] ?? null) : Promise.resolve(null),
    invoice.brand_id ? api.select<BrandRow>("brands", { filters: [["id", "eq", invoice.brand_id]] }).then((r) => r[0] ?? null) : Promise.resolve(null),
    invoice.deal_id ? api.select<DealRow>("deals", { filters: [["id", "eq", invoice.deal_id]] }).then((r) => r[0] ?? null) : Promise.resolve(null),
    invoice.period_id ? api.select<PeriodRow>("deal_periods", { filters: [["id", "eq", invoice.period_id]] }).then((r) => r[0] ?? null) : Promise.resolve(null),
    invoice.period_id
      ? api.select<InvoiceRow>("invoices", { filters: [["period_id", "eq", invoice.period_id], ["id", "neq", id]] })
      : invoice.deal_id ? api.select<InvoiceRow>("invoices", { filters: [["deal_id", "eq", invoice.deal_id], ["id", "neq", id]] }) : Promise.resolve([]),
    api.select<OrgPaperRow>("org_settings", { filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null).catch(() => null),
    api.select<BankRow>("org_bank_accounts", { filters: [["org_id", "eq", orgId]] }).catch(() => []),
    api.select<MemberRow>("memberships", { columns: "user_id, display_name", filters: [["org_id", "eq", orgId]] }).catch(() => []),
  ]);
  const brandId = invoice.brand_id ?? deal?.brand_id ?? null;
  const brand = brandFromRow ?? (brandId ? (await api.select<BrandRow>("brands", { filters: [["id", "eq", brandId]] }))[0] ?? null : null);
  const billingAgency = brand?.invoice_via_agency && brand.represented_by
    ? await api.select<BrandRow>("brands", { filters: [["id", "eq", brand.represented_by]] }).then((r) => r[0] ?? null).catch(() => null)
    : null;
  const linkCols = "id, number, issue_date, status, gross_cents, currency, sent_at";
  const creditFor = (invoice as { credit_for?: string | null }).credit_for ?? null;
  const [payments, creditNotes, original, collective] = await Promise.all([
    api.select<PaymentRow>("invoice_payments", { filters: byInvoice, order: [["created_at", "asc"]] }).catch(() => []),
    api.select<LinkedInvoice>("invoices", { columns: linkCols, filters: [["credit_for", "eq", id]] }).catch(() => []),
    creditFor ? api.select<LinkedInvoice>("invoices", { columns: `${linkCols}, self_billed, paid_cents, paid_at, admin_payment_confirmed_at`, filters: [["id", "eq", creditFor]] }).then((r) => r[0] ?? null).catch(() => null) : Promise.resolve(null),
    api.select<{ period_id: string }>("invoice_periods", { columns: "period_id", filters: byInvoice }).catch(() => [] as { period_id: string }[]),
  ]);
  const collectivePeriods = await collectiveMonths(collective.map((c) => c.period_id));
  // 0080: Belege der zugehörigen Creator-Rechnungen, z. B. Hotel bei der Auslagen-Weiterberechnung an die Marke
  const creatorInvoices = invoice.kind === "agency_to_brand" ? related.filter((r) => r.kind === "talent_to_agency" || r.kind === "talent_to_brand").map((r) => r.id) : [];
  const relatedAttachments = creatorInvoices.length
    ? await api.select<AttachmentRow>("attachments", { filters: [["owner_kind", "eq", "invoice"], ["owner_id", "in", creatorInvoices], ["deleted_at", "is", null]] }).then((r) => r.map(mitServerPruefung)).catch(() => [])
    : [];
  const lang = invoice["document_language"] === "en" ? "en" : invoice["document_language"] === "de" ? "de"
    : (invoice.kind === "talent_to_agency" || invoice.kind === "agency_to_talent" ? org?.default_document_language : brand?.language ?? org?.default_document_language) === "en" ? "en" : "de";
  const [legal, payout, noticeText, payoutInfo] = await Promise.all([
    api.select<Record<string, unknown>>("org_legal", { filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null).catch(() => null),
    invoice.self_billed && invoice.talent_id && invoice.status === "draft"
      ? api.select<Record<string, unknown>>("talent_payout_details", { filters: [["talent_id", "eq", invoice.talent_id]] }).then((r) => r[0] ?? null).catch(() => null)
      : Promise.resolve(null),
    invoice["notice_key"]
      ? api.read<{ body: string }[]>("org_text", { p_org: orgId, p_key: `invoice.notice.${String(invoice["notice_key"])}`, p_lang: lang })
          .then((r) => (Array.isArray(r) && r[0]?.body ? r[0].body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : null)).catch(() => null)
      : Promise.resolve(null),
    invoice.kind === "talent_to_agency"
      ? api.read<PayoutInfo[]>("invoice_payout_info", { p_invoices: [invoice.id] }).then((r) => (Array.isArray(r) ? r[0] ?? null : null)).catch(() => null)
      : Promise.resolve(null),
  ]);
  const [contacts, talentDeals, talentPeriods, taxHistory, dealItems] = await Promise.all([
    brandId ? api.select<ContactRow>("brand_contacts", { filters: [["brand_id", "eq", brandId]] }).catch(() => []) : Promise.resolve([]),
    invoice.talent_id ? api.select<DealRow>("deals", { filters: [["talent_id", "eq", invoice.talent_id], ["org_id", "eq", orgId]], order: [["created_at", "desc"]] }) : Promise.resolve([]),
    invoice.talent_id
      ? api.select<DealRow>("deals", { columns: "id", filters: [["talent_id", "eq", invoice.talent_id], ["org_id", "eq", orgId]] })
          .then((ds) => (ds.length ? api.select<PeriodRow>("deal_periods", { filters: [["deal_id", "in", ds.map((d) => d.id)]], order: [["month", "asc"]] }) : []))
      : Promise.resolve([]),
    invoice.talent_id
      ? api.select<{ small_business: boolean; valid_from: string }>("tax_status_history", { columns: "small_business, valid_from", filters: [["talent_id", "eq", invoice.talent_id]] }).catch(() => [])
      : Promise.resolve([]),
    invoice.deal_id ? api.select<LineItemRow>("deal_line_items", { filters: [["deal_id", "eq", invoice.deal_id], ["org_id", "eq", orgId]] }).catch(() => [] as LineItemRow[]) : Promise.resolve([] as LineItemRow[]),
  ]);
  // L14: nur unterschriebene Verträge zählen; ohne Leserecht auf Verträge gilt die Frist als nicht vereinbart (vorsichtig)
  const contractBodies = invoice.deal_id && (invoice.kind === "agency_to_brand" || invoice.kind === "talent_to_brand")
    ? await api.select<{ body_html: string | null }>("contracts", { columns: "body_html", filters: [["deal_id", "eq", invoice.deal_id], ["org_id", "eq", orgId], ["status", "eq", "signed"]] })
        .then((r) => r.map((c) => c.body_html ?? "")).catch(() => [] as string[])
    : [];
  return { invoice, lines, events, attachments, talent, brand, deal, period, related, talentDeals, talentPeriods, dealItems, org, bank, contacts, members, payments, creditNotes, original,
    collectivePeriods, legal, payout, noticeText, payoutInfo, taxHistory, billingAgency, relatedAttachments, contractBodies };
}

/** Sprache des Belegs: eingefroren, sonst Rechnungen an Marken in deren Sprache, sonst die Dokumentsprache der Agentur (WS-03) */
export function docLanguage(d: InvoiceDetail): "de" | "en" {
  const frozen = d.invoice["document_language"];
  if (frozen === "en" || frozen === "de") return frozen;
  const orgLang = d.org?.default_document_language === "en" ? "en" : "de";
  if (d.invoice.kind === "talent_to_agency" || d.invoice.kind === "agency_to_talent") return orgLang;
  return d.brand?.language === "en" ? "en" : d.brand?.language === "de" ? "de" : orgLang;
}

/** Eingaben für den Beleg (model/invoice-document.ts) aus den geladenen Daten der Rechnung */
/** Aus einer Gutschrift bereits ausgezahlt (wie loadInvoicePdf auf dem Server); nur bei Gutschriften, sonst unbekannt */
function paidOutOf(o: LinkedInvoice): number | null {
  if (!o.self_billed) return null;
  const confirmed = o.paid_at != null || o.admin_payment_confirmed_at != null;
  return Math.max(Number(o.paid_cents ?? 0), confirmed ? Number(o.gross_cents ?? 0) : 0);
}

export function docInputsOf(d: InvoiceDetail, orgName: string, logo?: PdfImage | null): DocInputs {
  const inv = d.invoice;
  const agency = inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent";
  const bank = d.bank.find((x) => x.currency === inv.currency && x.is_default) ?? d.bank.find((x) => x.currency === inv.currency);
  const talent = talentPartyOf(d.talent as unknown as Record<string, unknown> | null);
  return {
    invoice: inv as unknown as Record<string, unknown>, lines: d.lines,
    org: orgPartyOf(d.org as Record<string, unknown> | null, d.legal, orgName),
    talent, brand: brandPartyOf(d.brand as unknown as Record<string, unknown> | null, d.deal as unknown as Record<string, unknown> | null),
    bankFallback: agency
      ? orgBankOf(bank, d.org as Record<string, unknown> | null)
      : inv.self_billed ? payoutBankOf(d.payout, talent?.name ?? null) : null,
    noticeFallback: d.noticeText,
    collectiveMonths: d.collectivePeriods.map((p) => p.month),
    original: d.original ? { number: d.original.number, issue_date: d.original.issue_date, paid_out_cents: paidOutOf(d.original) } : null,
    language: docLanguage(d),
    logo: logo ?? null,
  };
}

/** Monate einer Sammelrechnung mit Kooperation, nach Monat sortiert. */
async function collectiveMonths(periodIds: string[]): Promise<InvoiceDetail["collectivePeriods"]> {
  if (!periodIds.length) return [];
  const periods = await api.select<{ id: string; month: string; deal_id: string }>("deal_periods", { columns: "id, month, deal_id", filters: [["id", "in", periodIds]] }).catch(() => []);
  const dealIds = [...new Set(periods.map((p) => p.deal_id))];
  const deals = dealIds.length ? await api.select<{ id: string; title: string }>("deals", { columns: "id, title", filters: [["id", "in", dealIds]] }).catch(() => []) : [];
  const title = new Map(deals.map((d) => [d.id, d.title]));
  return periods.map((p) => ({ period_id: p.id, month: p.month, deal_id: p.deal_id, deal_title: title.get(p.deal_id) ?? "" }))
    .sort((a, b) => a.month.localeCompare(b.month) || a.deal_title.localeCompare(b.deal_title));
}

export function useInvoiceDetail(orgId: string, id: string) {
  return useQuery({ queryKey: ["invoice_detail", orgId, id], queryFn: () => loadInvoiceDetail(orgId, id) });
}

export const EVENT_LABEL: Record<string, string> = {
  get created() { return t("Angelegt", "Created"); },
  get created_draft() { return t("Entwurf angelegt", "Draft created"); },
  get created_submitted() { return t("Angelegt und eingereicht", "Created and submitted"); },
  get created_approved() { return t("Angelegt und festgeschrieben", "Created and issued"); },
  get draft() { return t("Wieder in Bearbeitung", "Back in progress"); },
  get submitted() { return t("Eingereicht", "Submitted"); },
  // Praxistest K-056: Anmerkung des Creators beim Einreichen (details.text)
  get comment() { return t("Anmerkung des Creators", "Creator's note"); },
  get submission_withdrawn() { return t("Einreichung vom Creator zurückgenommen", "Submission withdrawn by the creator"); },
  get withdrawn() { return t("Vom Creator zurückgezogen", "Withdrawn by the creator"); },
  get discarded() { return t("Verworfen", "Discarded"); },
  get cancelled() { return t("Storniert", "Cancelled"); },
  get payment_report_retracted() { return t("Zahlungsmeldung vom Creator zurückgenommen", "Payment report withdrawn by the creator"); },
  get payment_still_overdue() { return t("Creator meldet: Geld fehlt noch", "Creator reports: money still missing"); },
  get approved() { return t("Freigegeben", "Approved"); },
  get revision_requested() { return t("Zur Korrektur zurückgegeben", "Returned for correction"); },
  get rejected() { return t("Abgelehnt", "Declined"); },
  get rejection_reverted() { return t("Ablehnung zurückgenommen, wieder in Prüfung", "Rejection withdrawn, back in review"); },
  get sent_to_brand() { return t("An die Marke gesendet", "Sent to the brand"); },
  get resent_to_brand() { return t("Erneut an die Marke gesendet", "Sent to the brand again"); },
  get dunning_sent() { return t("Mahnschreiben gesendet", "Dunning letter sent"); },
  get collection() { return t("An Inkasso übergeben", "Handed over to debt collection"); },
  get collection_withdrawn() { return t("Inkasso zurückgenommen", "Debt collection withdrawn"); },
  get correction_mail_resent() { return t("Korrektur-Mail erneut gesendet", "Correction email sent again"); },
  get payment_check_requested() { return t("Beim Creator nachgefragt", "Queried with the creator"); },
  get payment_reported() { return t("Zahlung vom Creator gemeldet", "Payment reported by the creator"); },
  get payment_report_rejected() { return t("Zahlungsmeldung zurückgewiesen", "Payment report rejected"); },
  get payment_confirmed() { return t("Zahlung bestätigt", "Payment confirmed"); },
  get payment_reset() { return t("Zahlung zurückgesetzt", "Payment reset"); },
  get payout_confirmed() { return t("Auszahlung bestätigt, Creator gefragt", "Payout confirmed, creator asked"); },
  get payout_revoked() { return t("Auszahlungsbestätigung zurückgenommen", "Payout confirmation withdrawn"); },
  get payment_recorded() { return t("Zahlungseingang erfasst", "Incoming payment recorded"); },
  get due_extended() { return t("Frist verlängert", "Deadline extended"); },
  get dunning_blocked() { return t("Mahnsperre gesetzt", "Dunning block set"); },
  get dunning_unblocked() { return t("Mahnsperre aufgehoben", "Dunning block lifted"); },
  get dunning_recorded() { return t("Mahnstufe nachgetragen", "Dunning level recorded"); },
  get attachment_added() { return t("Beleg angehängt", "Receipt attached"); },
  get dunning_reset() { return t("Mahnstufe zurückgesetzt", "Dunning level reset"); },
  get created_credit_note() { return t("Stornorechnung erstellt", "Cancellation invoice created"); },
  get self_billing_objected() { return t("Creator hat der Gutschrift widersprochen", "Creator objected to the self-billing invoice"); },
  get approval_retracted() { return t("Freigabe zurückgezogen", "Approval withdrawn"); },
  get sent_to_talent() { return t("An den Creator gesendet", "Sent to the creator"); },
  get resent_to_talent() { return t("Erneut an den Creator gesendet", "Sent to the creator again"); },
  get paid() { return t("Bezahlt", "Paid"); },
  get mail_failed() { return t("Mail nicht zugestellt", "Email not delivered"); },
  status: "Status geändert",
  // 0055
  get pdf_archived() { return t("PDF archiviert", "PDF archived"); },
  get einvoice_archived() { return t("E-Rechnung (XRechnung) archiviert", "E-invoice (XRechnung) archived"); },
  // Steuerprüfung L12 (0446)
  get einvoice_received() { return t("E-Rechnung empfangen (Original mit Prüfsumme abgelegt)", "E-invoice received (original stored with checksum)"); },
  // E-1 (0448)
  get einvoice_server_checked() { return t("E-Rechnung serverseitig geprüft (Prüfsumme und Werte bestätigt)", "E-invoice verified on the server (checksum and values confirmed)"); },
  get einvoice_browser_mismatch() { return t("E-Rechnung serverseitig geprüft: Angaben des Browsers wichen ab, es gelten die Werte des Servers", "E-invoice verified on the server: the browser's details differed, the server values apply"); },
  get einvoice_server_check_failed() { return t("Serverseitige Prüfung der E-Rechnung fehlgeschlagen", "Server-side check of the e-invoice failed"); },
  get delivered() { return t("Rechnungsmail zugestellt", "Invoice email delivered"); },
  get delivery_recorded() { return t("Zugang vermerkt", "Receipt noted"); },
  get delivery_corrected() { return t("Zugangstag berichtigt", "Date of receipt corrected"); },
  // 0085: Rechnungsmail zurückgekommen, der Zugang gilt nicht mehr (AUTOMATIK-13)
  get delivery_bounced() { return t("Rechnungsmail zurückgekommen, Zugang zurückgenommen", "Invoice email bounced, receipt withdrawn"); },
  get payout_advance() { return t("Vorab ausgezahlt (Marke hatte noch nicht gezahlt)", "Paid out in advance (brand had not paid yet)"); },
  get payout_queued() { return t("In den Zahlungslauf gegeben", "Put into the payment run"); },
  // 0083 (GELD-22)
  get payout_partial() { return t("Anteilig in den Auszahlungslauf (Marke hat erst teilweise gezahlt)", "Partially put into the payout run (brand has only paid in part)"); },
  get payout_partial_paid() { return t("Anteil ausgezahlt, Rest offen bis zur Zahlung der Marke", "Share paid out, rest open until the brand pays"); },
  get payout_auto_confirmed() { return t("Ohne Rückmeldung des Creators als bezahlt gebucht", "Booked as paid without the creator's response"); },
  get offset() { return t("Verrechnet", "Offset"); },
  get bad_debt() { return t("Als uneinbringlich ausgebucht", "Written off as uncollectible"); },
  get overpayment_settled() { return t("Mehrbetrag erstattet bzw. verrechnet", "Overpayment refunded or offset"); },
  get paid_via_creator() { return t("Marke hat an den Creator gezahlt", "Brand has paid the creator"); },
  get settled_via_brand_payment() { return t("Verrechnet: Marke zahlte direkt an den Creator", "Offset: brand paid the creator directly"); },
  get approved_with_findings() { return t("Trotz Prüfhinweisen freigegeben", "Approved despite review notices"); },
  // K-160: Weiterleitung an die Buchhaltung (0161) mit Klartext statt technischem Schlüssel
  get accounting_forwarded() { return t("An die Buchhaltung weitergeleitet", "Forwarded to accounting"); },
  get cost_note_created() { return t("Beleg über Verzugskosten erstellt", "Late-payment cost note created"); },
  get self_billing_accepted() { return t("Gutschrift vom Creator angenommen", "Self-billing invoice accepted by the creator"); },
  // 0064
  get imported() { return t("Aus dem bisherigen System übernommen", "Taken over from the previous system"); },
  get pdf_attached() { return t("Original-PDF hinterlegt", "Original PDF stored"); },
};

/**
 * SAAS-3: Empfänger ist eine Privatperson (Verbraucher)? Maßgeblich ist der eingefrorene Empfänger der Rechnung
 * (recipient_snapshot.business = "false"); fehlt die Angabe dort, die Marke (brands.is_business = false).
 */
/**
 * Steuerprüfung L14: Zahlungsfrist dieser Rechnung vertraglich vereinbart? Nur mit unterschriebenem Vertrag zur Kooperation, dessen
 * Zahlungsklausel (0313 § 5) höchstens die Frist der Rechnung nennt. Sonst beginnt der Verzug für Zinsen und Pauschale erst mit der
 * ersten Mahnung nach Fälligkeit oder 30 Tage nach Zugang (computeDunningCosts, paymentTermAgreed: false).
 */
export function zahlungsfristVereinbart(d: Pick<InvoiceDetail, "invoice" | "contractBodies">): boolean {
  const i = d.invoice as unknown as { payment_term_days?: number | null; issue_date?: string | null; document_due_date?: string | null };
  return paymentTermAgreed({
    contractDays: agreedPaymentTermDays(d.contractBodies ?? []), invoiceTermDays: i.payment_term_days ?? null,
    issueDate: i.issue_date ?? null, dueDate: i.document_due_date ?? null,
  });
}

export function recipientIsConsumer(d: { invoice: object; brand: object | null }): boolean {
  const raw = (d.invoice as Record<string, unknown>).recipient_snapshot;
  const snap = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  const b = snap?.business;
  if (b === "false" || b === false) return true;
  if (b === "true" || b === true) return false;
  return (d.brand as Record<string, unknown> | null)?.is_business === false;
}
